import { useRef } from 'react';
import type { ChangeEvent, MouseEvent, ReactNode } from 'react';
import type { ButtonProps } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Typography from '@mui/material/Typography';

import { FileWrapper } from 'helpers/classes';

export type AcceptFileExtension =
  | '.png'
  | '.jpg'
  | '.jpeg'
  | '.pdf'
  | '.doc'
  | '.docx';

export type AcceptFileMimeType =
  | 'application/msword'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export type AcceptFileValue =
  | 'audio/*'
  | 'video/*'
  | 'image/*'
  | AcceptFileExtension
  | AcceptFileMimeType;

type Props = {
  children: ReactNode;
  /** Indica si es posible aceptar selección múltiple de archivo */
  multiple?: boolean;
  /** Arreglo con extensiones, formatos, mimetypes, etc. que acepta el input */
  accept?: AcceptFileValue[];
  /** Evento para traer archivos seleccionados */
  onSelectFiles: (files: FileWrapper[]) => void;
  /** Error a mostrar en caso que sea necesario, similar al de los inputs */
  errorMsg?: string;
} & ButtonProps;

export function FileInputButton({
  children,
  multiple = false,
  accept,
  onSelectFiles,
  errorMsg = '',
  sx = {},
  ...buttonProps
}: Props) {
  const input = useRef<any>();

  const clickHandler = (
    event: MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    input.current.click();
    if (buttonProps?.onClick) {
      buttonProps.onClick(event);
    }
  };

  const changeInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const wrapperFiles = Object.keys(event.target.files).map(
      (f) => new FileWrapper(event.target.files[Number(f)])
    );
    onSelectFiles(wrapperFiles);
    event.target.value = '';
  };

  return (
    <Box
      sx={
        errorMsg?.length > 0
          ? {
              backgroundColor: '#fff',
              transition: 'box-shadow 300ms',
              borderRadius: '4px',
              boxShadow:
                '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
              color: 'rgba(0,0,0,0.6)'
            }
          : {}
      }
    >
      <Button
        {...buttonProps}
        onClick={clickHandler}
        sx={
          errorMsg?.length > 0
            ? {
                ...sx,
                border: 'solid #d32f2f 1px'
              }
            : {
                ...sx
              }
        }
      >
        {children}
      </Button>
      {errorMsg?.length > 0 ? (
        <Typography
          component="p"
          sx={{
            fontWeight: '400',
            fontSize: '0.75rem',
            lineHeight: 1.66,
            letterSpacing: 0.03333,
            textAlign: 'left',
            marginTop: '4px',
            marginRight: '14px',
            marginBottom: 0,
            marginLeft: '14px',
            color: '#d32f2f'
          }}
        >
          {errorMsg}
        </Typography>
      ) : null}

      <Input
        inputProps={{ accept: accept?.join(', '), multiple }}
        inputRef={input}
        onChange={changeInputHandler}
        sx={{ display: 'none' }}
        type="file"
      />
    </Box>
  );
}

export default FileInputButton;
